import { FormattedEvent } from '@eventbrite/event-renderer';
import { GenericLazyString, gettext } from '@eventbrite/i18n';
import { differenceBy, slice } from 'lodash';

interface QuestionAndAnswerProps {
    isOnline: boolean;
    price?: GenericLazyString | string;
    format?: GenericLazyString | string;
    date?: GenericLazyString | string;
    city?: GenericLazyString | string;
    locationSlug?: string;
    rawDates?: string[];
}

export type DateFilterType = 'today' | 'this_weekend' | 'default';

export const getDateFilterType = (rawDates?: string[]): DateFilterType => {
    if (rawDates?.includes('today')) {
        return 'today';
    }
    if (rawDates?.includes('this_weekend')) {
        return 'this_weekend';
    }
    return 'default';
};

export const getSortedOrganicEvents = (organicEvents: FormattedEvent[]) => {
    const filteredOrganicEvents = organicEvents.filter((obj) => {
        return obj.startDate && obj.startTime;
    });
    const sortedOrganicEvents = filteredOrganicEvents.sort((a, b) => {
        /* eslint-disable  @typescript-eslint/no-non-null-assertion */
        // We are sure that startDate will have value as it is filtered in the previous step
        const dt_a = new Date(a.startDate!.concat(' ', a.startTime));
        const dt_b = new Date(b.startDate!.concat(' ', b.startTime));
        if (dt_a < dt_b) {
            return -1;
        } else if (dt_a > dt_b) {
            return 1;
        } else {
            return 0;
        }
    });
    return sortedOrganicEvents;
};

export const getBestEvents = (
    organicEvents: FormattedEvent[],
    nearApproachingEvents: FormattedEvent[],
) => {
    /*
        Best events are populated based on the below simple logic

        nearApproachingEvents list are the first four events of organic events which are sorted based on the start date and time.
        We exclude the nearApproachingEvents list from the original organic events list and get the other four events as best events.
        If the difference does not have sufficient(four) events in that case we get the short of events
        from nearApproachingEvents list.
    */
    const remainingEvents = differenceBy(
        organicEvents,
        nearApproachingEvents,
        'id',
    );
    let bestEvents: FormattedEvent[];
    if (remainingEvents.length >= 4) {
        bestEvents = slice(remainingEvents, 0, 4);
    } else {
        bestEvents = [
            ...remainingEvents,
            ...nearApproachingEvents.slice(0, 4 - remainingEvents.length),
        ];
    }
    return bestEvents;
};

export const getBestEventsFaq = (props: QuestionAndAnswerProps) => {
    const { isOnline, format, date, price, city, rawDates } = props;
    const dateFilterType = getDateFilterType(rawDates);
    let questionText, answerText;

    if (isOnline) {
        // Translators: "What are the best online events to attend?"
        // Translators: "What are the best free online events to attend today?"
        // Translators: "What are the best free online class events to attend?"
        questionText = gettext(
            'What are the best %(price)s online %(format)s events to attend %(date)s?',
            {
                price,
                format,
                date,
            },
        );
        // Translators: "According to Eventbrite, the best online events are:"
        // Translators: "According to Eventbrite, the best free online class events today are:"
        answerText = gettext(
            'According to Eventbrite, the best %(price)s online %(format)s events %(date)s are: ',
            {
                price,
                format,
                date,
            },
        );
    } else if (dateFilterType === 'today') {
        // Translators: "What are the best events to attend today in New York?"
        questionText = gettext(
            'What are the best events to attend today in %(city)s?',
            { city },
        );
        // Translators: "The best events to attend today in New York range from live music..."
        answerText = gettext(
            'The best events to attend today in %(city)s range from live music and nightlife experiences to food and drink events, seasonal activities, and cultural shows happening across the city today. According to Eventbrite, some of the best events today include:',
            { city },
        );
    } else if (dateFilterType === 'this_weekend') {
        // Translators: "What are the best events to attend this weekend in New York?"
        questionText = gettext(
            'What are the best events to attend this weekend in %(city)s?',
            { city },
        );
        // Translators: "The best events to attend this weekend in New York include concerts..."
        answerText = gettext(
            'The best events to attend this weekend in %(city)s include concerts, nightlife experiences, seasonal events, food and drink tastings, and live entertainment happening across the city. According to Eventbrite, some of the best events this weekend include:',
            { city },
        );
    } else {
        // Translators: "What are the best events to attend?"
        // Translators: "What are the best events to attend today?"
        // Translators: "What are the best class events to attend?"
        // Translators: "What are the best free class events to attend?"
        // Translators: "What are the best free events to attend?"
        questionText = gettext(
            'What are the best %(price)s %(format)s events to attend %(date)s?',
            {
                price,
                format,
                date,
            },
        );
        // Translators: "According to Eventbrite, the best events are:"
        // Translators: "According to Eventbrite, the best events today are:"
        // Translators: "According to Eventbrite, the best free class events are:"
        answerText = gettext(
            'According to Eventbrite, the best %(price)s %(format)s events %(date)s are:',
            {
                price,
                format,
                date,
            },
        );
    }
    return { questionText, answerText };
};

export const getTrendingEventsFaq = (props: QuestionAndAnswerProps) => {
    const { isOnline, city, rawDates } = props;
    const dateFilterType = getDateFilterType(rawDates);
    let questionText, answerText;

    if (isOnline) {
        questionText = gettext('Which events are currently trending online?');
        answerText = gettext('The trending searches online this month are:');
    } else if (dateFilterType === 'today') {
        // Custom content for "today" filter
        questionText = gettext(
            'Which events are currently trending in %(city)s?',
            { city },
        );
        answerText = gettext(
            'Events trending in %(city)s often reflect what people are going out for right now, including nightlife, live entertainment, seasonal celebrations, and food and drink experiences. The trending searches this month in %(city)s are:',
            { city },
        );
    } else if (dateFilterType === 'this_weekend') {
        // Custom content for "this weekend" filter
        questionText = gettext(
            'Which events are currently trending in %(city)s?',
            { city },
        );
        answerText = gettext(
            'Trending weekend events in %(city)s often reflect seasonal moments, nightlife demand, and popular neighborhoods, including concerts, parties, and special themed events. The trending searches this month in %(city)s are:',
            { city },
        );
    } else {
        // Default behavior
        questionText = gettext(
            'Which events are currently trending in %(city)s?',
            { city },
        );
        answerText = gettext(
            'The trending searches this month in %(city)s are:',
            { city },
        );
    }
    return { questionText, answerText };
};

export const getUpComingEventsFaq = (props: QuestionAndAnswerProps) => {
    const { isOnline, format, price, city, rawDates } = props;
    const dateFilterType = getDateFilterType(rawDates);
    let questionText, answerText;

    if (isOnline) {
        questionText = gettext(
            'Which %(price)s online %(format)s events are coming up?',
            {
                price,
                format,
            },
        );
        answerText = gettext(
            "Discover the best %(price)s online %(format)s events happening soon – you don't want to miss them:",
            {
                price,
                format,
            },
        );
    } else if (dateFilterType === 'today') {
        // Custom content for "today" filter
        questionText = gettext(
            'Which events are coming up in %(city)s today?',
            { city },
        );
        answerText = gettext(
            "Upcoming events in %(city)s include concerts, comedy shows, food and drink experiences, seasonal pop-ups, and nightlife events happening later this week and beyond. Discover some upcoming events you won't want to miss:",
            { city },
        );
    } else if (dateFilterType === 'this_weekend') {
        // Custom content for "this weekend" filter
        questionText = gettext(
            'Which events are coming up in %(city)s this weekend?',
            { city },
        );
        answerText = gettext(
            "In addition to what's happening right now, there are many upcoming weekend events in %(city)s, including live shows, parties, food experiences, and limited-time events. Discover upcoming weekend events you won't want to miss:",
            { city },
        );
    } else {
        // Default behavior
        questionText = gettext(
            'Which %(price)s %(format)s events are coming up?',
            {
                price,
                format,
            },
        );
        answerText = gettext(
            "Discover the best %(price)s %(format)s events happening soon – you don't want to miss them:",
            {
                price,
                format,
            },
        );
    }
    return { questionText, answerText };
};

export const getFreeEventsFaq = (props: QuestionAndAnswerProps) => {
    const { isOnline, city, locationSlug, rawDates } = props;
    const dateFilterType = getDateFilterType(rawDates);
    let questionText, answerText, linkText, linkUrl;

    if (isOnline) {
        return null; // No free events FAQ for online
    }

    const slug = locationSlug || 'local';

    if (dateFilterType === 'today') {
        questionText = gettext(
            'Are there free things to do in %(city)s today?',
            { city },
        );
        answerText = gettext(
            'Yes, there are often free things to do in %(city)s today, including live music, cultural events, seasonal activities, and community experiences across the city. Many events on Eventbrite are free to attend or offer free entry options. These are some free events happening in %(city)s today:',
            { city },
        );
        linkText = gettext('Free Events today');
        linkUrl = `/d/${slug}/free--events--today/`;
    } else if (dateFilterType === 'this_weekend') {
        questionText = gettext(
            'Are there free things to do in %(city)s this weekend?',
            { city },
        );
        answerText = gettext(
            'Yes — there are often free things to do in %(city)s this weekend, including live music, cultural events, seasonal activities, and community gatherings. Many events on Eventbrite are free or offer free entry options. These are some free events happening in %(city)s this weekend:',
            { city },
        );
        linkText = gettext('Free Events this weekend');
        linkUrl = `/d/${slug}/free--events--this-weekend/`;
    } else {
        return null; // No free events FAQ for default
    }

    return { questionText, answerText, linkText, linkUrl };
};
